import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import {
  applyActionCode,
  confirmPasswordReset,
  verifyPasswordResetCode,
} from "firebase/auth";
import { auth } from "../../firebase";
import useReveal from "../../components/useReveal";
import Spinner from "../../components/Spinner";

/* ── Icons ── */
const CheckIcon = () => (
  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);
const ErrorIcon = () => (
  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);
const EyeIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
  </svg>
);
const EyeOffIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

/* ── Password requirements ── */
const pwdRules = [
  { label: "8+ characters",        test: (p) => p.length >= 8 },
  { label: "Uppercase letter",     test: (p) => /[A-Z]/.test(p) },
  { label: "Lowercase letter",     test: (p) => /[a-z]/.test(p) },
  { label: "Number",               test: (p) => /\d/.test(p) },
];

export default function AuthActionPage() {
  useReveal();
  const [searchParams] = useSearchParams();
  const mode    = searchParams.get("mode")    || "";
  const oobCode = searchParams.get("oobCode") || "";

  /* ── Shared state ── */
  const [status, setStatus]   = useState("loading"); // loading | form | success | error
  const [message, setMessage] = useState("");

  /* ── Reset-password state ── */
  const [resetEmail, setResetEmail] = useState("");
  const [pwd,  setPwd]  = useState("");
  const [pwd2, setPwd2] = useState("");
  const [showPwd,  setShowPwd]  = useState(false);
  const [showPwd2, setShowPwd2] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  /* ── On mount — handle mode ── */
  useEffect(() => {
    if (!oobCode) { setStatus("error"); setMessage("Invalid or missing action code."); return; }

    if (mode === "resetPassword") {
      verifyPasswordResetCode(auth, oobCode)
        .then((email) => { setResetEmail(email); setStatus("form"); })
        .catch(() => { setStatus("error"); setMessage("This password reset link has expired or already been used."); });
    } else if (mode === "verifyEmail") {
      applyActionCode(auth, oobCode)
        .then(() => { setStatus("success"); setMessage("Your email has been verified. You can now sign in."); })
        .catch(() => { setStatus("error"); setMessage("This verification link has expired or already been used."); });
    } else if (mode === "recoverEmail") {
      applyActionCode(auth, oobCode)
        .then(() => { setStatus("success"); setMessage("Your email address has been restored. Check your inbox to reset your password if needed."); })
        .catch(() => { setStatus("error"); setMessage("This recovery link has expired or already been used."); });
    } else {
      setStatus("error");
      setMessage("Unknown action. Please request a new link.");
    }
  }, []); // run once on mount — oobCode and mode come from URL and never change

  /* ── Submit new password ── */
  const handleReset = async (e) => {
    e.preventDefault();
    if (pwd !== pwd2) { setMessage("Passwords don't match."); return; }
    if (!pwdRules.every((r) => r.test(pwd))) { setMessage("Password doesn't meet all requirements."); return; }
    setMessage("");
    setSubmitting(true);
    try {
      await confirmPasswordReset(auth, oobCode, pwd);
      setStatus("success");
      setMessage("Password updated! You can now sign in with your new password.");
    } catch {
      setStatus("error");
      setMessage("Failed to reset password. Please request a new reset link.");
    } finally {
      setSubmitting(false);
    }
  };

  /* ── Styles ── */
  const inputStyle = {
    width: "100%", padding: "12px 16px",
    background: "var(--bg-surface)", border: "1px solid var(--border-light)",
    borderRadius: 12, color: "var(--text-primary)", fontSize: 15,
    fontFamily: "inherit", outline: "none", transition: "border-color .2s",
  };
  const labelStyle = {
    display: "block", fontSize: 13, fontWeight: 600,
    color: "var(--text-secondary)", marginBottom: 8, letterSpacing: "-.01em",
  };

  /* ── Render helpers ── */
  const renderLoading = () => (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, padding: "40px 0" }}>
      <Spinner />
      <span style={{ fontSize: 13, color: "var(--text-muted)", letterSpacing: "-.01em" }}>Loading…</span>
    </div>
  );

  const renderSuccess = () => (
    <div style={{ textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: 20 }}>
      <div style={{ color: "#34d399" }}><CheckIcon /></div>
      <p style={{ fontSize: 15, color: "var(--text-secondary)", lineHeight: 1.7 }}>{message}</p>
      <Link to="/login" className="btn btn-white btn-md" style={{ marginTop: 8 }}>Go to sign in</Link>
    </div>
  );

  const renderError = () => (
    <div style={{ textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: 20 }}>
      <div style={{ color: "#f87171" }}><ErrorIcon /></div>
      <p style={{ fontSize: 15, color: "var(--text-secondary)", lineHeight: 1.7 }}>{message}</p>
      <Link to="/login" className="btn btn-white btn-md" style={{ marginTop: 8 }}>Back to sign in</Link>
    </div>
  );

  const renderResetForm = () => (
    <form onSubmit={handleReset} style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <p style={{ fontSize: 14, color: "var(--text-secondary)", margin: 0, lineHeight: 1.6 }}>
        Setting a new password for <strong style={{ color: "var(--text-primary)" }}>{resetEmail}</strong>
      </p>

      <div>
        <label style={labelStyle}>New password</label>
        <div style={{ position: "relative" }}>
          <input
            type={showPwd ? "text" : "password"} value={pwd}
            onChange={(e) => setPwd(e.target.value)} placeholder="At least 8 characters"
            style={{ ...inputStyle, paddingRight: 44 }}
            onFocus={(e) => (e.target.style.borderColor = "rgba(99,102,241,.5)")}
            onBlur={(e)  => (e.target.style.borderColor = "var(--border-light)")}
          />
          <button type="button" tabIndex={-1} onClick={() => setShowPwd((s) => !s)}
            style={{ position: "absolute", top: "50%", right: 14, transform: "translateY(-50%)", background: "none", border: "none", outline: "none", padding: 0, cursor: "pointer", color: "var(--text-tertiary)", display: "flex", alignItems: "center", justifyContent: "center", lineHeight: 0 }}>
            {showPwd ? <EyeOffIcon /> : <EyeIcon />}
          </button>
        </div>
        {/* Requirements checklist */}
        {pwd.length > 0 && (
          <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 6 }}>
            {pwdRules.map((r) => {
              const ok = r.test(pwd);
              return (
                <div key={r.label} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: ok ? "#34d399" : "var(--text-tertiary)" }}>
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    {ok
                      ? <><circle cx="6" cy="6" r="6" fill="#34d399" /><path d="M3.5 6l1.8 1.8 3.2-3.6" stroke="#fff" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></>
                      : <circle cx="6" cy="6" r="5.5" stroke="currentColor" strokeOpacity=".4"/>}
                  </svg>
                  {r.label}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div>
        <label style={labelStyle}>Confirm password</label>
        <div style={{ position: "relative" }}>
          <input
            type={showPwd2 ? "text" : "password"} value={pwd2}
            onChange={(e) => setPwd2(e.target.value)} placeholder="Repeat your password"
            style={{ ...inputStyle, paddingRight: 44 }}
            onFocus={(e) => (e.target.style.borderColor = "rgba(99,102,241,.5)")}
            onBlur={(e)  => (e.target.style.borderColor = "var(--border-light)")}
          />
          <button type="button" tabIndex={-1} onClick={() => setShowPwd2((s) => !s)}
            style={{ position: "absolute", top: "50%", right: 14, transform: "translateY(-50%)", background: "none", border: "none", outline: "none", padding: 0, cursor: "pointer", color: "var(--text-tertiary)", display: "flex", alignItems: "center", justifyContent: "center", lineHeight: 0 }}>
            {showPwd2 ? <EyeOffIcon /> : <EyeIcon />}
          </button>
        </div>
      </div>

      {message && (
        <div style={{ background: "rgba(248,113,113,.1)", border: "1px solid rgba(248,113,113,.25)", borderRadius: 10, padding: "10px 16px", fontSize: 13, color: "#f87171" }}>
          {message}
        </div>
      )}

      <button type="submit" className="btn btn-white btn-lg btn-full"
        disabled={submitting || !pwdRules.every((r) => r.test(pwd)) || pwd !== pwd2}
        style={{ marginTop: 4, opacity: (submitting || !pwdRules.every((r) => r.test(pwd)) || pwd !== pwd2) ? 0.5 : 1 }}>
        {submitting ? "Saving…" : "Set new password"}
      </button>
    </form>
  );

  const titles = {
    resetPassword: "Reset your password",
    verifyEmail:   "Verify your email",
    recoverEmail:  "Recover your email",
  };

  return (
    <div style={{ background: "var(--bg-primary)", paddingTop: 80, minHeight: "100vh" }}>
      <section className="section section-dark">
        <div style={{ maxWidth: 440, margin: "0 auto", padding: "0 2rem" }}>
          <div style={{ textAlign: "center", marginBottom: 40 }}>
            <div className="reveal eyebrow">Developer API</div>
            <h1 className="reveal headline" style={{ fontSize: "clamp(28px,5vw,40px)", marginBottom: 12 }}>
              {titles[mode] || "Account action"}
            </h1>
          </div>

          <div className="reveal" style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            {status === "loading" && renderLoading()}
            {status === "form"    && renderResetForm()}
            {status === "success" && renderSuccess()}
            {status === "error"   && renderError()}
          </div>
        </div>
      </section>
    </div>
  );
}
